import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import adminApi from '../../api/client';
import MergeToolPublishers from './MergeToolPublishers';
import LinkingAssistant from './LinkingAssistant';

interface Publisher {
  id: string;
  name_en: string | null;
  name_si: string | null;
  is_verified: boolean;
  is_active: boolean;
  linked_books_count: number;
  created_at: string | null;
}

interface PaginatedPublishers {
  total: number;
  page: number;
  per_page: number;
  items: Publisher[];
}

const Toast = ({ message, type, onClose }: { message: string, type: 'success' | 'error', onClose: () => void }) => {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);
  
  if (!message) return null;
  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded shadow-lg text-white font-medium z-50 ${type === 'success' ? 'bg-[#1D9E75]' : 'bg-[#D85A30]'}`}>
      {message}
    </div>
  );
};

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, text, confirmText, confirmColor }: any) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <h3 className="text-xl font-bold text-[#085041] mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{text}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
          <button 
            onClick={onConfirm} 
            className={`px-4 py-2 text-white rounded hover:bg-opacity-90 font-medium bg-[${confirmColor}]`}
            style={{ backgroundColor: confirmColor }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const RejectModal = ({ isOpen, onClose, onConfirm, title }: any) => {
  const [reason, setReason] = useState("");
  useEffect(() => { if (isOpen) setReason(""); }, [isOpen]);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <h3 className="text-xl font-bold text-[#085041] mb-4">{title}</h3>
        <textarea
          className="w-full border border-gray-300 rounded p-3 mb-4 focus:outline-none focus:border-[#1D9E75] min-h-[120px] resize-none"
          placeholder="Reason for rejection..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim()}
            className="px-4 py-2 bg-[#D85A30] text-white rounded hover:bg-opacity-90 disabled:opacity-50 font-medium"
          >
            Confirm Reject
          </button>
        </div>
      </div>
    </div>
  );
};

const PublisherModal = ({ isOpen, onClose, onSave, publisher }: { isOpen: boolean, onClose: () => void, onSave: (data: any) => void, publisher: Publisher | null }) => {
  const [formData, setFormData] = useState({
    name_en: '',
    name_si: '',
    is_verified: false,
    is_active: true
  });

  useEffect(() => {
    if (publisher) {
      setFormData({
        name_en: publisher.name_en || '',
        name_si: publisher.name_si || '',
        is_verified: publisher.is_verified || false,
        is_active: publisher.is_active !== false // default true
      });
    } else {
      setFormData({ name_en: '', name_si: '', is_verified: false, is_active: true });
    }
  }, [publisher, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-xl">
        <h3 className="text-xl font-bold text-[#085041] mb-4">{publisher ? 'Edit Publisher' : 'Add Publisher'}</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name (English)</label>
            <input type="text" className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75]" value={formData.name_en} onChange={e => setFormData({...formData, name_en: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name (Sinhala)</label>
            <input type="text" className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75]" value={formData.name_si} onChange={e => setFormData({...formData, name_si: e.target.value})} />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.is_verified} onChange={e => setFormData({...formData, is_verified: e.target.checked})} />
              <span className="text-sm">Is Verified</span>
            </label>
            {publisher && (
              <label className="flex items-center gap-2 text-[#D85A30]">
                <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
                <span className="text-sm">Is Active</span>
              </label>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
          <button onClick={() => onSave(formData)} className="px-4 py-2 bg-[#085041] text-white rounded hover:bg-opacity-90 font-medium">Save</button>
        </div>
      </div>
    </div>
  );
};

export default function PublishersPanel() {
  const [searchParams] = useSearchParams();
  const [activeView, setActiveView] = useState<'browse' | 'pending' | 'merge' | 'link'>('browse');
  useEffect(() => {
    if (searchParams.get('view') === 'pending') setActiveView('pending');
  }, [searchParams]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 20;

  const [browseData, setBrowseData] = useState<PaginatedPublishers>({ total: 0, page: 1, per_page: 20, items: [] });
  const [pendingData, setPendingData] = useState<Publisher[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({ show: false, message: '', type: 'success' });
  const [editModal, setEditModal] = useState<{show: boolean, publisher: Publisher | null}>({ show: false, publisher: null });
  const [deleteModal, setDeleteModal] = useState<{show: boolean, id: string | null}>({ show: false, id: null });
  const [rejectModal, setRejectModal] = useState<{show: boolean, id: string | null}>({ show: false, id: null });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchBrowse = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await adminApi.get(`/admin/master/publishers?q=${debouncedSearch}&page=${page}&per_page=${perPage}`);
      setBrowseData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch publishers');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page]);

  const fetchPending = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await adminApi.get('/admin/publishers/pending');
      setPendingData(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch pending publishers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeView === 'browse') fetchBrowse();
    else fetchPending();
  }, [activeView, fetchBrowse, fetchPending]);

  const showToast = (message: string, type: 'success' | 'error') => setToast({ show: true, message, type });

  const handleSavePublisher = async (data: any) => {
    try {
      const payload = {
        name_en: data.name_en || null,
        name_si: data.name_si || null,
        is_verified: data.is_verified,
        ...(editModal.publisher && { is_active: data.is_active })
      };

      if (editModal.publisher) {
        await adminApi.patch(`/admin/master/publishers/${editModal.publisher.id}`, payload);
        showToast('Publisher updated successfully', 'success');
      } else {
        await adminApi.post('/admin/master/publishers', payload);
        showToast('Publisher added successfully', 'success');
      }
      setEditModal({ show: false, publisher: null });
      fetchBrowse();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to save publisher', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.id) return;
    try {
      await adminApi.delete(`/admin/master/publishers/${deleteModal.id}`);
      showToast('Publisher deactivated successfully', 'success');
      setDeleteModal({ show: false, id: null });
      fetchBrowse();
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to deactivate publisher';
      showToast(detail, 'error');
      setError(detail);
      setDeleteModal({ show: false, id: null });
    }
  };

  const handleVerify = async (id: string) => {
    try {
      await adminApi.post(`/admin/publishers/${id}/verify`);
      showToast('Publisher verified', 'success');
      fetchPending();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to verify', 'error');
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectModal.id) return;
    try {
      await adminApi.post(`/admin/publishers/${rejectModal.id}/reject`, { reason });
      showToast('Publisher rejected', 'success');
      setRejectModal({ show: false, id: null });
      fetchPending();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to reject', 'error');
      setRejectModal({ show: false, id: null });
    }
  };

  const totalPages = Math.ceil((browseData.total || 0) / perPage);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex bg-gray-200 rounded-full p-1">
          <button 
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${activeView === 'browse' ? 'bg-white text-[#085041] shadow' : 'text-gray-500'}`}
            onClick={() => setActiveView('browse')}
          >
            Browse All
          </button>
          <button 
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${activeView === 'pending' ? 'bg-white text-[#085041] shadow' : 'text-gray-500'}`}
            onClick={() => setActiveView('pending')}
          >
            Pending Review
          </button>
          <button 
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${activeView === 'merge' ? 'bg-white text-[#085041] shadow' : 'text-gray-500'}`}
            onClick={() => setActiveView('merge')}
          >
            Merge Duplicates
          </button>
          <button 
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${activeView === 'link' ? 'bg-white text-[#085041] shadow' : 'text-gray-500'}`}
            onClick={() => setActiveView('link')}
          >
            Link Records
          </button>
        </div>

        {activeView === 'browse' && (
          <button 
            onClick={() => setEditModal({ show: true, publisher: null })}
            className="px-4 py-2 bg-[#085041] text-white rounded font-medium hover:bg-opacity-90"
          >
            + Add Publisher
          </button>
        )}
      </div>

      {error && <div className="bg-[#D85A30]/10 border border-[#D85A30] text-[#D85A30] px-4 py-3 rounded mb-6">{error}</div>}

      {activeView === 'browse' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <input 
              type="text" 
              placeholder="Search publishers..." 
              className="w-full md:w-1/3 border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-sm border-b border-gray-200">
                  <th className="p-4 font-semibold">Name</th>
                  <th className="p-4 font-semibold">Verified</th>
                  <th className="p-4 font-semibold">Linked Books</th>
                  <th className="p-4 font-semibold">Created</th>
                  <th className="p-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && browseData.items.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-gray-500">Loading...</td></tr>
                ) : browseData.items.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-gray-500">No records found</td></tr>
                ) : (
                  browseData.items.map(publisher => (
                    <tr key={publisher.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div className="font-medium text-gray-900">{publisher.name_en || '—'}</div>
                        <div className="text-xs text-gray-500">{publisher.name_si || '—'}</div>
                      </td>
                      <td className="p-4">
                        {publisher.is_verified ? '✅' : '⚠️'}
                      </td>
                      <td className="p-4 text-gray-600">{publisher.linked_books_count}</td>
                      <td className="p-4 text-sm text-gray-500">{publisher.created_at ? new Date(publisher.created_at).toLocaleDateString() : '—'}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => setEditModal({ show: true, publisher })} className="text-[#085041] hover:underline mr-4 font-medium text-sm">Edit</button>
                        <button onClick={() => setDeleteModal({ show: true, id: publisher.id })} className="text-[#D85A30] hover:underline font-medium text-sm">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && totalPages > 1 && (
            <div className="p-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
              <div>Total: {browseData.total}</div>
              <div className="flex gap-2">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                <span className="px-3 py-1">Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeView === 'pending' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-sm border-b border-gray-200">
                  <th className="p-4 font-semibold">Name</th>
                  <th className="p-4 font-semibold">Created</th>
                  <th className="p-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && pendingData.length === 0 ? (
                  <tr><td colSpan={3} className="p-8 text-center text-gray-500">Loading...</td></tr>
                ) : pendingData.length === 0 ? (
                  <tr><td colSpan={3} className="p-8 text-center text-gray-500">No records found</td></tr>
                ) : (
                  pendingData.map(publisher => (
                    <tr key={publisher.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div className="font-medium text-gray-900">{publisher.name_en || '—'}</div>
                        <div className="text-xs text-gray-500 mb-1">{publisher.name_si || '—'}</div>
                        <div className="text-xs text-[#BA7517] font-semibold mt-1">Awaiting verification</div>
                      </td>
                      <td className="p-4 text-sm text-gray-500">{publisher.created_at ? new Date(publisher.created_at).toLocaleDateString() : '—'}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => handleVerify(publisher.id)} className="bg-[#1D9E75] text-white px-3 py-1 rounded text-sm font-medium mr-2 hover:bg-opacity-90">Verify</button>
                        <button onClick={() => setRejectModal({ show: true, id: publisher.id })} className="border border-[#D85A30] text-[#D85A30] px-3 py-1 rounded text-sm font-medium hover:bg-[#D85A30] hover:text-white transition-colors">Reject</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeView === 'merge' && (
        <MergeToolPublishers />
      )}

      {activeView === 'link' && (
        <LinkingAssistant entityType="publishers" />
      )}

      <Toast message={toast.show ? toast.message : ''} type={toast.type} onClose={() => setToast(prev => ({ ...prev, show: false }))} />
      
      <PublisherModal 
        isOpen={editModal.show} 
        publisher={editModal.publisher} 
        onClose={() => setEditModal({ show: false, publisher: null })} 
        onSave={handleSavePublisher} 
      />
      
      <ConfirmModal 
        isOpen={deleteModal.show} 
        title="Deactivate Publisher"
        text="Deactivate this publisher? This does not delete data, only hides it from search. (Fails if books are linked — use Merge Duplicates instead.)"
        confirmText="Deactivate"
        confirmColor="#D85A30"
        onClose={() => setDeleteModal({ show: false, id: null })}
        onConfirm={handleDelete}
      />
      
      <RejectModal
        isOpen={rejectModal.show}
        onClose={() => setRejectModal({ show: false, id: null })}
        onConfirm={handleReject}
        title="Reject Pending Publisher"
      />
    </div>
  );
}
